import { jsx, jsxs, Fragment } from "react/jsx-runtime";
import { useTranslations, useLanguage } from "@vocab/react";
import { Alert, Stack, Text, Box, Dropdown, IconSubCategory, RadioGroup, RadioItem, Loader, FieldMessage } from "braid-design-system";
import { forwardRef, useMemo, useState, useEffect, useImperativeHandle, useCallback } from "react";
import { translations as translations$5 } from "./.vocab/index.mjs";
import { translations } from "./components/CriticalErrorMessage/.vocab/index.mjs";
import { translations as translations$1 } from "./components/ManualCategorySelector/.vocab/index.mjs";
import { translations as translations$2 } from "./components/SuggestedCategoriesList/.vocab/index.mjs";
import { useGetSuggestedCategoriesLazyQuery, GetSuggestedCategoriesDocument } from "./graphql-types.chunk.mjs";
import { createValidator, runValidation, required } from "@seek/validators-js";
import { translations as translations$3 } from "./utils/.vocab/index.mjs";
import { translations as translations$4 } from "./hooks/useCategoryValidator/.vocab/index.mjs";
import { marketHierarchy } from "@seek/classification-data";
const CriticalErrorMessage = () => {
  const { t } = useTranslations(translations);
  return /* @__PURE__ */ jsx(Alert, { tone: "critical", children: /* @__PURE__ */ jsxs(Stack, { space: "medium", children: [
    /* @__PURE__ */ jsx(Text, { weight: "strong", children: t("critical-error-message-heading") }),
    /* @__PURE__ */ jsxs(Stack, { space: "small", children: [
      /* @__PURE__ */ jsx(Text, { children: t("critical-error-message-text") }),
      /* @__PURE__ */ jsx(Text, { children: t("critical-error-message-cta") })
    ] })
  ] }) });
};
const ManualCategorySelector = ({
  categoryOptions,
  categoryValue,
  inputIdPrefix,
  isCategoryValid,
  isSubCategoryValid,
  validationMessage,
  onCategoryChange,
  onSubCategoryChange,
  subCategoryOptions,
  subCategoryValue
}) => {
  const { t } = useTranslations(translations$1);
  return /* @__PURE__ */ jsx(Box, { maxWidth: "small", children: /* @__PURE__ */ jsxs(Stack, { space: "xsmall", children: [
    /* @__PURE__ */ jsx(
      Dropdown,
      {
        id: `${inputIdPrefix}-job-category`,
        "aria-label": t("manual-category-selector-category-aria-label"),
        data: { testid: `${inputIdPrefix}-job-category` },
        onChange: onCategoryChange,
        placeholder: t("manual-category-selector-category-placeholder"),
        value: categoryValue ?? "",
        tone: isCategoryValid ? "neutral" : "critical",
        message: !isCategoryValid ? validationMessage : void 0,
        children: (categoryOptions || []).map(
          ({ label, value }, index) => /* @__PURE__ */ jsx("option", { value, children: label }, `manual-category-${index}`)
        )
      }
    ),
    subCategoryOptions && subCategoryOptions.length > 0 ? /* @__PURE__ */ jsx(
      Dropdown,
      {
        id: `${inputIdPrefix}-job-sub-category`,
        icon: /* @__PURE__ */ jsx(IconSubCategory, {}),
        data: { testid: `${inputIdPrefix}-job-sub-category` },
        "aria-label": t("manual-category-selector-sub-category-aria-label"),
        onChange: onSubCategoryChange,
        placeholder: t("manual-category-selector-sub-category-placeholder"),
        value: subCategoryValue ?? "",
        tone: isSubCategoryValid ? "neutral" : "critical",
        message: !isSubCategoryValid ? validationMessage : void 0,
        children: subCategoryOptions.map(
          ({ label, value }, index) => /* @__PURE__ */ jsx("option", { value, children: label }, `manual-sub-category-${index}`)
        )
      }
    ) : null
  ] }) });
};
const LOCALE_LANGUAGE = "en";
const OTHER_OPTION_NAME = "other";
const OTHER_OPTION_ID = -1;
const getRadioButtonValue = (selectedSuggestedCategory, isInManualSelectionMode) => isInManualSelectionMode ? OTHER_OPTION_ID.toString() : selectedSuggestedCategory.toString();
const SuggestedCategoriesList = ({
  inputIdPrefix,
  isInManualSelectionMode,
  onSuggestedCategoryChange,
  selectedSuggestedCategory,
  suggestedCategories,
  isValid
}) => {
  const { t } = useTranslations(translations$2);
  return /* @__PURE__ */ jsxs(Stack, { space: "small", children: [
    /* @__PURE__ */ jsx(Text, { tone: "secondary", children: t("suggested-category-sub-heading") }),
    /* @__PURE__ */ jsx(
      RadioGroup,
      {
        id: `${inputIdPrefix}-suggested-categories`,
        "aria-label": t("suggested-category-list-aria-label"),
        value: getRadioButtonValue(
          selectedSuggestedCategory,
          isInManualSelectionMode
        ),
        onChange: onSuggestedCategoryChange,
        tone: Boolean(isValid) ? "neutral" : "critical",
        children: suggestedCategories.map(
          ({ category }, index) => /* @__PURE__ */ jsx(
            RadioItem,
            {
              label: category.subCategoryName ? `${category.subCategoryName} (${category.categoryName})` : category.categoryName,
              value: category.subCategoryId.toString(),
              data: {
                "category-id": category.categoryId,
                "category-name": category.categoryName,
                "subcategory-id": category.subCategoryId,
                "subcategory-name": category.subCategoryName,
                cy: "suggested-job-category-radio",
                testid: `${inputIdPrefix}-suggested-categories`
              }
            },
            `suggested-job-category-${index}`
          )
        )
      }
    )
  ] });
};
const validateFields = (validationRules, language) => {
  const runValidationInput = validationRules.reduce(
    (result, curr) => {
      result.fields[curr.field] = curr.value;
      if (!result.rules[curr.field]) {
        result.rules[curr.field] = { validators: [] };
      }
      result.rules[curr.field].validators = curr.validators;
      result.rules[curr.field].validatorProps = curr.validatorProps;
      return result;
    },
    { fields: {}, rules: {} }
  );
  const runValidationResult = runValidation(
    {
      fields: runValidationInput.fields,
      rules: runValidationInput.rules
    },
    language
  );
  const validationResult = {};
  if (runValidationResult) {
    Object.keys(runValidationInput.fields).map((fieldName) => {
      var _a;
      const errorMessage = (_a = runValidationResult.errors.find(
        (error) => error.fieldName === fieldName
      )) == null ? void 0 : _a.message;
      validationResult[fieldName] = errorMessage;
    });
  }
  return validationResult;
};
const { suggestedSubCategoryIdRequired } = createValidator({
  test: ({ value }) => Boolean(value),
  formatErrorMessages: (messages) => () => messages["validation-error-suggested-category"].format(),
  validatorName: "suggestedSubCategoryIdRequired",
  translations: translations$3
});
const useCategoryValidator = () => {
  const { t } = useTranslations(translations$4);
  const { language } = useLanguage();
  const handleValidation = ({
    selectedOption,
    selectedCategory,
    selectedSubCategory
  }) => {
    let isCategoryValid = true;
    let isSubCategoryValid = true;
    let isSuggestedCategoryValid = true;
    let message;
    const suggestedSubCategoryId = selectedOption == null ? void 0 : selectedOption.subCategoryId;
    const validationResult = validateFields(
      [
        {
          field: "suggestedSubCategoryId",
          validators: [suggestedSubCategoryIdRequired],
          value: suggestedSubCategoryId
        },
        {
          field: "otherCategoryId",
          validators: [required],
          validatorProps: {
            requiredLabel: t("validation-error-other-category-label")
          },
          value: selectedCategory
        },
        {
          field: "otherSubCategoryId",
          validators: [required],
          validatorProps: {
            requiredLabel: t("validation-error-other-sub-category-label")
          },
          value: selectedSubCategory
        }
      ],
      language
    );
    const suggestedSubCategoryIdErrorMessage = validationResult.suggestedSubCategoryId;
    const otherCategoryIdErrorMessage = validationResult.otherCategoryId;
    const otherSubCategoryIdErrorMessage = validationResult.otherSubCategoryId;
    if (suggestedSubCategoryIdErrorMessage) {
      isSuggestedCategoryValid = false;
      message = suggestedSubCategoryIdErrorMessage;
    } else if (suggestedSubCategoryId === OTHER_OPTION_ID) {
      if (otherCategoryIdErrorMessage) {
        isCategoryValid = false;
        message = otherCategoryIdErrorMessage;
      } else if (!otherCategoryIdErrorMessage && otherSubCategoryIdErrorMessage) {
        isSubCategoryValid = false;
        message = otherSubCategoryIdErrorMessage;
      }
    }
    return {
      isCategoryValid,
      isSubCategoryValid,
      isSuggestedCategoryValid,
      message
    };
  };
  return { handleValidation };
};
function getAllCategories(classificationMarket, language = "en") {
  return {
    categories: marketHierarchy(classificationMarket).filter((c) => !c.parent).reduce(
      (acc, cat) => acc.concat(
        ...[
          cat.subClassifications.reduce(
            (subAcc, subCat) => subAcc.concat([
              {
                subCategoryId: subCat.shortId,
                subCategoryName: subCat.name(language),
                categoryId: cat.shortId,
                categoryName: cat.name(language)
              }
            ]),
            []
          )
        ]
      ),
      []
    )
  };
}
const getSortedCategoryIds = (categories, language) => Object.entries(categories).map(([catId, cat]) => ({ id: catId, name: cat.category.categoryName })).sort(
  (a, b) => a.name.localeCompare(b.name, language)
).map((pair) => pair.id);
function reduceToHierarchy(catHash, cat) {
  if (catHash[cat.categoryId]) {
    const topCategory = catHash[cat.categoryId];
    const hasSubcategory = Boolean(
      topCategory.subCategories.find(
        (elem) => elem.subCategoryId === cat.subCategoryId
      )
    );
    if (!hasSubcategory) {
      topCategory.subCategories = topCategory.subCategories.concat([cat]);
    }
  } else {
    catHash[cat.categoryId] = {
      category: cat,
      subCategories: [cat]
    };
  }
  return catHash;
}
function sortSubCategoriesByName(subCategories, language) {
  subCategories.sort((a, b) => {
    if (a.subCategoryName.toLowerCase() === OTHER_OPTION_NAME) {
      return 1;
    }
    if (b.subCategoryName.toLowerCase() === OTHER_OPTION_NAME) {
      return -1;
    }
    return a.subCategoryName.localeCompare(b.subCategoryName, language);
  });
}
function getCategoryHierarchy(categories, language) {
  const cats = categories.reduce(
    reduceToHierarchy,
    {}
  );
  const categoryIds = getSortedCategoryIds(cats, language);
  categoryIds.forEach((categoryId) => {
    sortSubCategoriesByName(cats[categoryId].subCategories, language);
  });
  return cats;
}
const getSelectedClassification = (hierarchy, selectedCategory, selectedSubCategory) => {
  var _a;
  return (_a = hierarchy[selectedCategory]) == null ? void 0 : _a.subCategories.find(
    (item) => item.subCategoryId.toString() === selectedSubCategory
  );
};
const log = {
  error: (...message) => console.error(...message),
  newline: () => console.log(),
  ok: (...message) => console.log(...message),
  plain: (...message) => console.log(...message),
  warn: (...message) => console.warn(...message)
};
const JobCategory = forwardRef(
  ({
    country = "au",
    inputIdPrefix = "",
    jobTitle = "",
    language = LOCALE_LANGUAGE,
    onCategoryChange,
    onSubCategoryChange,
    onSuggestedCategoryChange,
    preSelectedCategoryId,
    preSelectedSubCategoryId,
    reserveMessageSpace
  }, ref) => {
    const { t } = useTranslations(translations$5);
    const otherJobCategoryName = t("job-category-other-label");
    const otherCategoryOption = useMemo(
      () => ({
        category: {
          categoryId: OTHER_OPTION_ID,
          categoryName: otherJobCategoryName,
          subCategoryId: OTHER_OPTION_ID,
          subCategoryName: ""
        },
        probability: 0
      }),
      [otherJobCategoryName]
    );
    const [selectedCategory, setSelectedCategory] = useState("");
    const [selectedSubCategory, setSelectedSubCategory] = useState("");
    const [selectedOption, setSelectedOption] = useState();
    const [allCategoryIds, setAllCategoryIds] = useState([]);
    const [hierarchy, setHierarchy] = useState(null);
    const [suggestedCategories, setSuggestedCategories] = useState(
      []
    );
    useEffect(() => {
      const categoryResponse = getAllCategories(
        country.toUpperCase(),
        // @TODO: Is uppercasing here appropriate?
        language
      );
      if (categoryResponse && categoryResponse.categories) {
        const catHierarchy = getCategoryHierarchy(
          categoryResponse.categories,
          language
        );
        setHierarchy(catHierarchy);
        const sortedCats = getSortedCategoryIds(
          catHierarchy,
          language
        );
        setAllCategoryIds(sortedCats);
      }
    }, [language, country]);
    const [
      loadSuggestions,
      { loading: suggestedCatsLoading, error: suggestedCatsError }
    ] = useGetSuggestedCategoriesLazyQuery({
      fetchPolicy: "cache-and-network",
      variables: {
        jobTitle,
        country,
        language
      },
      onCompleted: (data) => {
        if (data.suggestedCategories) {
          setSuggestedCategories(data.suggestedCategories);
        }
      },
      onError: (error) => {
        log.error("JobCategory | GetSuggestedCategories query failed: ", error);
      }
    });
    useImperativeHandle(ref, () => ({
      handleValidation: (skipIfNull = false) => handleInputValidation(
        selectedOption,
        selectedCategory,
        selectedSubCategory,
        skipIfNull
      )
    }));
    useEffect(() => {
      if (jobTitle) {
        loadSuggestions();
      }
    }, [jobTitle]);
    const [isCategoryValid, setIsCategoryValid] = useState(true);
    const [isSubCategoryValid, setIsSubCategoryValid] = useState(true);
    const [isSuggestedCategoryValid, setIsSuggestedCategoryValid] = useState(true);
    const [validationMessage, setValidationMessage] = useState();
    const { handleValidation } = useCategoryValidator();
    const handleInputValidation = useCallback(
      (selOption = selectedOption, selCategory = selectedCategory, selSubCategory = selectedSubCategory, skipIfNull = false) => {
        if (!skipIfNull) {
          const validationResult = handleValidation({
            selectedOption: selOption,
            selectedCategory: selCategory,
            selectedSubCategory: selSubCategory
          });
          setValidationResult(validationResult);
          return validationResult.message;
        }
      },
      [handleValidation, selectedCategory, selectedOption, selectedSubCategory]
    );
    const [categoryOptions, setCategoryOptions] = useState([]);
    useEffect(
      () => setCategoryOptions([...suggestedCategories, otherCategoryOption]),
      [suggestedCategories, otherCategoryOption]
    );
    useEffect(() => {
      var _a;
      if (hierarchy) {
        const defaultSuggestedSubCategory = suggestedCategories.length > 0 ? suggestedCategories[0].category : otherCategoryOption.category;
        if (preSelectedCategoryId && preSelectedSubCategoryId && preSelectedCategoryId !== String(OTHER_OPTION_ID) && preSelectedSubCategoryId !== String(OTHER_OPTION_ID)) {
          const preSelectedSuggestedCategory = suggestedCategories.find(
            (item) => item.category.subCategoryId.toString() === preSelectedSubCategoryId
          );
          const selectedSuggestedSubCategoryId = ((_a = preSelectedSuggestedCategory == null ? void 0 : preSelectedSuggestedCategory.category) == null ? void 0 : _a.subCategoryId) ?? OTHER_OPTION_ID;
          if (selectedSuggestedSubCategoryId === OTHER_OPTION_ID) {
            setIsInManualSelectionMode(true);
            setSelectedOption(otherCategoryOption.category);
            handleSuggestedCategoryChange(
              void 0,
              otherCategoryOption.category
            );
            setSelectedCategory(preSelectedCategoryId);
            handleCategoryChange(
              void 0,
              preSelectedCategoryId,
              preSelectedSubCategoryId,
              otherCategoryOption.category
            );
            const firstSubCatId = getDefaultSubCategoryId(
              preSelectedCategoryId
            );
            setSelectedSubCategory(preSelectedSubCategoryId ?? firstSubCatId);
            handleSubCategoryChange(
              void 0,
              preSelectedCategoryId,
              preSelectedSubCategoryId,
              otherCategoryOption.category
            );
          } else {
            setSelectedSubCategory(
              selectedSuggestedSubCategoryId.toString() ?? defaultSuggestedSubCategory.subCategoryId.toString()
            );
            const suggestedCategory = (preSelectedSuggestedCategory == null ? void 0 : preSelectedSuggestedCategory.category) ?? defaultSuggestedSubCategory;
            setSelectedOption(suggestedCategory);
            handleSuggestedCategoryChange(void 0, suggestedCategory);
          }
        } else {
          setSelectedSubCategory(
            defaultSuggestedSubCategory.subCategoryId.toString()
          );
          setSelectedOption(defaultSuggestedSubCategory);
          handleSuggestedCategoryChange(void 0, defaultSuggestedSubCategory);
        }
      }
    }, [otherCategoryOption, suggestedCategories, hierarchy]);
    const [isSuggestError, setIsSuggestError] = useState(false);
    useEffect(() => {
      if (!suggestedCatsLoading) {
        setIsSuggestError(Boolean(suggestedCatsError));
      }
    }, [suggestedCatsError, suggestedCatsLoading]);
    const [availableSubCategories, setAvailableSubCategories] = useState([]);
    const [isInManualSelectionMode, setIsInManualSelectionMode] = useState(false);
    function updateSuggestedCategorySelection(categoryDetails) {
      const isOtherOption = categoryDetails.categoryId === OTHER_OPTION_ID;
      if (isOtherOption) {
        setSelectedCategory("");
        setSelectedSubCategory("");
      } else {
        setAvailableSubCategories([]);
        setSelectedSubCategory(categoryDetails.subCategoryId.toString());
      }
      setIsInManualSelectionMode(isOtherOption);
      if (onSuggestedCategoryChange) {
        triggerJobCategoryChangeEvent(
          onSuggestedCategoryChange,
          categoryDetails,
          isOtherOption
        );
      }
    }
    const setValidationResult = ({
      isCategoryValid: isCatValid,
      isSubCategoryValid: isSubCatValid,
      isSuggestedCategoryValid: isSuggestedCatValid,
      message: validationMsg
    }) => {
      setIsCategoryValid(isCatValid);
      setIsSubCategoryValid(isSubCatValid);
      setIsSuggestedCategoryValid(isSuggestedCatValid);
      setValidationMessage(validationMsg);
    };
    function getDefaultSubCategoryId(selectedCat) {
      var _a;
      const availableSubCats = hierarchy[selectedCat].subCategories;
      setAvailableSubCategories(availableSubCats);
      return (_a = availableSubCats[0]) == null ? void 0 : _a.subCategoryId.toString();
    }
    const handleSuggestedCategoryChange = (e, updatedSuggestedCategory = selectedOption) => {
      let newSelectedCategory;
      if (e) {
        const { currentTarget } = e;
        const dataCategoryId = currentTarget.getAttribute("data-category-id");
        const dataCategoryName = currentTarget.getAttribute("data-category-name");
        const dataSubCategoryId = currentTarget.getAttribute(
          "data-subcategory-id"
        );
        const dataSubCategoryName = currentTarget.getAttribute(
          "data-subcategory-name"
        );
        newSelectedCategory = {
          categoryId: Number(dataCategoryId),
          categoryName: dataCategoryName ?? "",
          subCategoryId: Number(dataSubCategoryId),
          subCategoryName: dataSubCategoryName ?? ""
        };
        setSelectedOption(newSelectedCategory);
        updateSuggestedCategorySelection(newSelectedCategory);
      } else {
        newSelectedCategory = {
          categoryId: Number(updatedSuggestedCategory == null ? void 0 : updatedSuggestedCategory.categoryId),
          categoryName: (updatedSuggestedCategory == null ? void 0 : updatedSuggestedCategory.categoryName) ?? "",
          subCategoryId: Number(updatedSuggestedCategory == null ? void 0 : updatedSuggestedCategory.subCategoryId),
          subCategoryName: (updatedSuggestedCategory == null ? void 0 : updatedSuggestedCategory.subCategoryName) ?? ""
        };
        updateSuggestedCategorySelection(newSelectedCategory);
      }
      setValidationResult({
        isCategoryValid: true,
        isSubCategoryValid: true,
        isSuggestedCategoryValid: true,
        message: ""
      });
    };
    const handleCategoryChange = (e, updatedCategory = selectedCategory, updatedSubCategory = selectedSubCategory, updatedSelectedOption = selectedOption) => {
      const selectedCat = (e == null ? void 0 : e.currentTarget.value) || updatedCategory;
      setSelectedCategory(selectedCat);
      const subCategoryId = e ? getDefaultSubCategoryId(selectedCat) : updatedSubCategory;
      handleInputValidation(updatedSelectedOption, selectedCat, subCategoryId);
      if (onCategoryChange) {
        const selectedClassification = getSelectedClassification(
          hierarchy,
          selectedCat,
          subCategoryId
        );
        triggerJobCategoryChangeEvent(
          onCategoryChange,
          selectedClassification,
          isInManualSelectionMode
        );
      }
      handleSubCategoryChange(void 0, selectedCat, subCategoryId);
    };
    const handleSubCategoryChange = (e, updatedCategory = selectedCategory, updatedSubCategory = selectedSubCategory, updatedSelectedOption = selectedOption) => {
      const selectedSubCatId = (e == null ? void 0 : e.currentTarget.value) || updatedSubCategory;
      setSelectedSubCategory(selectedSubCatId);
      handleInputValidation(
        updatedSelectedOption,
        updatedCategory,
        selectedSubCatId
      );
      if (onSubCategoryChange) {
        const selectedClassification = getSelectedClassification(
          hierarchy,
          updatedCategory,
          selectedSubCatId
        );
        triggerJobCategoryChangeEvent(
          onSubCategoryChange,
          selectedClassification,
          isInManualSelectionMode
        );
      }
    };
    const triggerJobCategoryChangeEvent = (changeEvent, selectedCat, isInManualMode = isInManualSelectionMode) => changeEvent({
      category: selectedCat,
      isInManualSelectionMode: isInManualMode,
      suggestedCategoryList: suggestedCategories
    });
    const getCategoryOptions = () => allCategoryIds.map((catId) => ({
      label: hierarchy[catId].category.categoryName,
      value: catId.toString()
    }));
    const getSubCategoryOptions = () => availableSubCategories.map((category) => ({
      label: category.subCategoryName,
      value: category.subCategoryId.toString()
    }));
    const hasCriticalError = isSuggestError;
    const hasJobTitle = Boolean(jobTitle);
    const hasSuggestedCategories = Boolean(suggestedCategories.length);
    const isComponentVisible = hasJobTitle && (hasSuggestedCategories || isInManualSelectionMode || isSuggestError);
    const isSuggestedListVisible = hasJobTitle && hasSuggestedCategories;
    if (!isComponentVisible) {
      return /* @__PURE__ */ jsx(Fragment, {});
    }
    return /* @__PURE__ */ jsxs(Stack, { space: "small", children: [
      /* @__PURE__ */ jsx(Text, { weight: "strong", children: t("job-category-heading") }),
      hasCriticalError ? /* @__PURE__ */ jsx(CriticalErrorMessage, {}) : null,
      suggestedCatsLoading ? /* @__PURE__ */ jsx(Box, { paddingY: "medium", children: /* @__PURE__ */ jsx(Loader, { size: "small" }) }) : isSuggestedListVisible && /* @__PURE__ */ jsx(
        SuggestedCategoriesList,
        {
          inputIdPrefix,
          isInManualSelectionMode,
          isValid: isSuggestedCategoryValid,
          onSuggestedCategoryChange: handleSuggestedCategoryChange,
          selectedSuggestedCategory: selectedSubCategory,
          suggestedCategories: categoryOptions
        }
      ),
      isInManualSelectionMode || isSuggestError ? /* @__PURE__ */ jsx(
        ManualCategorySelector,
        {
          categoryOptions: getCategoryOptions(),
          categoryValue: selectedCategory,
          inputIdPrefix,
          isCategoryValid,
          isSubCategoryValid,
          onCategoryChange: handleCategoryChange,
          validationMessage,
          onSubCategoryChange: handleSubCategoryChange,
          subCategoryOptions: getSubCategoryOptions(),
          subCategoryValue: selectedSubCategory
        }
      ) : null,
      isSuggestedListVisible && !isSuggestedCategoryValid || reserveMessageSpace ? /* @__PURE__ */ jsx(
        FieldMessage,
        {
          id: "JobCategoryValidationMessage",
          tone: Boolean(validationMessage) ? "critical" : "neutral",
          message: validationMessage ?? void 0,
          reserveMessageSpace
        }
      ) : null
    ] });
  }
);
JobCategory.displayName = "JobCategory";
const mockCategoriesFromClassifications = marketHierarchy("AU").filter((c) => !c.parent).reduce(
  (acc, cat) => acc.concat(
    ...[
      cat.subClassifications.reduce(
        (subAcc, subCat) => subAcc.concat([
          {
            categoryId: cat.shortId,
            categoryName: cat.name(),
            subCategoryId: subCat.shortId,
            subCategoryName: subCat.name()
          }
        ]),
        []
      )
    ]
  ),
  []
);
const mockJobTitle = "Developer";
const mockCountry = "au";
const mockCategories = {
  categories: mockCategoriesFromClassifications
};
const mockSuggestedCategories = {
  suggestedCategories: [
    {
      category: {
        categoryId: 6281,
        categoryName: "Information & Communication Technology",
        subCategoryId: 6287,
        subCategoryName: "Developers/Programmers"
      },
      probability: 0.8736184
    },
    {
      category: {
        categoryId: 6281,
        categoryName: "Information & Communication Technology",
        subCategoryId: 6290,
        subCategoryName: "Engineering - Software"
      },
      probability: 0.0681419
    },
    {
      category: {
        categoryId: 6281,
        categoryName: "Information & Communication Technology",
        subCategoryId: 6302,
        subCategoryName: "Web Development & Production"
      },
      probability: 0.021034233
    }
  ]
};
const mockCategoriesRequest = {
  query: GetSuggestedCategoriesDocument,
  variables: {
    country: mockCountry
  }
};
const mockSuggestedCategoriesRequest = {
  query: GetSuggestedCategoriesDocument,
  variables: {
    jobTitle: mockJobTitle,
    country: mockCountry,
    language: "en"
  }
};
const mockCategoriesSuccessResponse = {
  request: mockCategoriesRequest,
  result: { data: mockCategories }
};
const mockSuggestedCategoriesSuccessResponse = {
  request: mockSuggestedCategoriesRequest,
  result: { data: mockSuggestedCategories }
};
const mockCategoriesForRoleTitle = [
  {
    ...mockCategoriesSuccessResponse
  },
  {
    ...mockSuggestedCategoriesSuccessResponse
  }
];
export {
  GetSuggestedCategoriesDocument,
  JobCategory,
  OTHER_OPTION_ID,
  mockCategoriesForRoleTitle,
  mockCategoriesFromClassifications
};
